import React from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { ASSETS } from './assets';

/**
 * ClosedBook — the studyfolio front cover as the literal cover of a 3D binder.
 * The cover is a rotating element with a FRONT face (cover art) and a BACK face
 * (cream inside page) so it can swing a full 180° and expose the inside naturally.
 *
 * Props:
 *   open           — boolean, when true animates the cover open to -178°
 *   dim            — 0..1 dim overlay over the cover art (during modal / message)
 *   showPlay       — boolean, show a play button on the cover (mobile CTA)
 *   onPlay         — callback when the play button is tapped
 */
export default function ClosedBook({
    open = false,
    dim = 0,
    showPlay = false,
    onPlay,
}) {
    const coverRotate = open ? -178 : 0;
    return (
        <div className="relative flex items-center justify-center" style={{ perspective: 2600 }}>
            <motion.div
                data-testid="closed-book"
                className="relative"
                style={{
                    width: 'min(440px, 60vh)',
                    maxWidth: '90vw',
                    aspectRatio: '3 / 4',
                    transformStyle: 'preserve-3d',
                    contain: 'layout',
                }}
            >
                {/* Contact shadow on the ground */}
                <div
                    className="absolute -bottom-10 left-1/2 -translate-x-1/2 rounded-full"
                    style={{
                        width: '88%',
                        height: '4rem',
                        background:
                            'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 70%)',
                        filter: 'blur(10px)',
                    }}
                />

                {/* Back board of the binder (revealed as cover opens) — cream first spread */}
                <div
                    className="absolute inset-0 rounded-lg overflow-hidden"
                    style={{
                        transform: 'translateZ(-16px)',
                        background: 'linear-gradient(180deg, #F1EAD1 0%, #E8DFC1 100%)',
                        boxShadow: 'inset 24px 0 50px -12px rgba(59,68,30,0.4)',
                    }}
                >
                    <div
                        className="absolute inset-0 opacity-25"
                        style={{
                            backgroundImage:
                                'repeating-linear-gradient(180deg, transparent 0, transparent 1.7rem, rgba(59,68,30,0.12) 1.7rem, rgba(59,68,30,0.12) calc(1.7rem + 1px))',
                        }}
                    />
                    <div className="absolute top-8 left-8 mono text-[10px] text-[color:var(--moss-deep)]">
                        Ex Libris — Studyfolio
                    </div>
                </div>

                {/* Page block edges on the right (stacked pages) */}
                <div
                    className="absolute top-2 bottom-2 rounded-sm overflow-hidden"
                    style={{
                        right: '-7px',
                        width: '12px',
                        transform: 'translateZ(-8px)',
                        background:
                            'repeating-linear-gradient(180deg, #F1EAD1 0px, #F1EAD1 1px, #DBD1AF 1px, #DBD1AF 2px)',
                        boxShadow: 'inset -2px 0 6px rgba(0,0,0,0.35)',
                    }}
                />

                {/* FRONT COVER — rotates open around the spine (left edge) */}
                <motion.div
                    className="absolute inset-0"
                    animate={{ rotateY: coverRotate }}
                    transition={{ duration: 1.4, ease: [0.72, 0, 0.28, 1] }}
                    style={{
                        transformOrigin: 'left center',
                        transformStyle: 'preserve-3d',
                    }}
                >
                    {/* FRONT FACE — cover art */}
                    <div
                        className="absolute inset-0 rounded-lg overflow-hidden"
                        style={{
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            boxShadow:
                                '0 40px 80px -25px rgba(0,0,0,0.6), 0 18px 40px -12px rgba(59,68,30,0.6), inset 0 0 0 1px rgba(0,0,0,0.3)',
                        }}
                    >
                        <img
                            src={ASSETS.cover}
                            alt="Studyfolio cover"
                            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                            draggable={false}
                        />
                        {/* top-left warm highlight */}
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{
                                background:
                                    'radial-gradient(90% 60% at 12% 8%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 55%)',
                                mixBlendMode: 'screen',
                            }}
                        />
                        {/* spine shadow */}
                        <div
                            className="pointer-events-none absolute inset-y-0 left-0 w-10"
                            style={{
                                background:
                                    'linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)',
                            }}
                        />
                        {/* right edge shadow */}
                        <div
                            className="pointer-events-none absolute inset-y-0 right-0 w-6"
                            style={{
                                background:
                                    'linear-gradient(-90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)',
                            }}
                        />
                        {/* cardstock grain */}
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{
                                backgroundImage:
                                    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                                opacity: 0.14,
                                mixBlendMode: 'multiply',
                            }}
                        />
                        {/* deboss vignette */}
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{ boxShadow: 'inset 0 0 90px rgba(0,0,0,0.35)' }}
                        />
                        {/* dim overlay */}
                        <motion.div
                            className="pointer-events-none absolute inset-0"
                            animate={{ opacity: dim }}
                            transition={{ duration: 0.6 }}
                            style={{ background: 'rgba(11,15,7,0.6)' }}
                        />
                        {/* Play button — mobile CTA, visible only when book is closed.
                           Rendered inside a centered flex layer so it sits exactly
                           at the geometric center of the cover regardless of art crop. */}
                        {showPlay && (
                            <div
                                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                data-testid="closed-book-play-layer"
                            >
                                <motion.button
                                    initial={{ opacity: 0, scale: 0.6 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.6 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                    whileTap={{ scale: 0.94 }}
                                    onClick={onPlay}
                                    data-testid="closed-book-play"
                                    aria-label="Open the folio"
                                    className="relative h-20 w-20 rounded-full flex items-center justify-center pointer-events-auto"
                                    style={{
                                        background:
                                            'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), var(--lavender) 60%)',
                                        boxShadow:
                                            '0 12px 32px -6px rgba(0,0,0,0.55), 0 0 0 6px rgba(183,156,255,0.35)',
                                        border: '2px solid rgba(255,255,255,0.75)',
                                    }}
                                >
                                    <motion.span
                                        className="absolute inset-0 rounded-full pointer-events-none"
                                        animate={{
                                            boxShadow: [
                                                '0 0 0 0 rgba(183,156,255,0.75)',
                                                '0 0 0 22px rgba(183,156,255,0)',
                                            ],
                                        }}
                                        transition={{ duration: 1.8, repeat: Infinity }}
                                    />
                                    <Play size={30} strokeWidth={2.2} className="text-[color:var(--moss-dark)] translate-x-[2px]" fill="currentColor" />
                                </motion.button>
                            </div>
                        )}
                    </div>

                    {/* BACK FACE — cream inside of the cover (visible after 90°) */}
                    <div
                        className="absolute inset-0 rounded-lg overflow-hidden"
                        style={{
                            transform: 'rotateY(180deg)',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            background: 'linear-gradient(180deg, #EFE7CD 0%, #E4DABB 100%)',
                            boxShadow: 'inset -24px 0 50px -12px rgba(59,68,30,0.4)',
                        }}
                    >
                        <div
                            className="absolute inset-0 opacity-25"
                            style={{
                                backgroundImage:
                                    'repeating-linear-gradient(180deg, transparent 0, transparent 1.7rem, rgba(59,68,30,0.1) 1.7rem, rgba(59,68,30,0.1) calc(1.7rem + 1px))',
                            }}
                        />
                        <div className="absolute top-8 right-8 mono text-[10px] text-[color:var(--moss-deep)] text-right">
                            This folio belongs to<br />a careful mind.
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}
